import { Router, useLocation } from "@reach/router"
import { differenceInDays } from "date-fns"
import { navigate } from "gatsby"
import { memo, useCallback, useEffect, useMemo } from "react"

import {
  ABTestContainer,
  AB_TESTS,
  AB_TESTS_DATA,
} from "@trueskin-web/ab-experiments"
import {
  consultationClient,
  orderClient,
  useQuery,
  userClient,
} from "@trueskin-web/apis"
import {
  Container,
  RouteGuard as GlobalRouteGuard,
  Seo,
  Spinner,
  useQueryState,
} from "@trueskin-web/components"
import { useAuth } from "@trueskin-web/context"
import { envDetails } from "@trueskin-web/core"
import {
  initialConsultationService,
  storageService,
  vwoService,
} from "@trueskin-web/services"
import { Routes } from "@trueskin-web/translations"

import {
  AntiAgingChoosePlan,
  AntiAgingPayment,
} from "../ab-experiments/anti-aging"
import {
  PlanSelectionStorylineImprovementV1,
  PlanSelectionStorylineImprovementV2,
  PlanSelectionStorylineImprovementV3,
} from "../ab-experiments/plan-selection-storyline-improvement"
import ProgressBar from "../ab-experiments/progress-bar"
import ChoosePlan from "../components/choose-plan"
import useCustomerMetadata from "../hooks/use-customer-metadata"
import Layout from "../layout"

import AppSelectionScreen from "./bd-welcome-screen"
import CreateAccount from "./create-account"
import DocumentsUpload from "./documents"
import Payment from "./payment"
import Photos from "./photos"
import Questionnaire from "./questionnaire"
import Shipping from "./shipping"
import TreatmentPlanNavigation from "./treatment-plan-navigation"
import WelcomeBack from "./welcome-back"

const storageKeys = storageService.getStorageKeys()
const storageTypes = storageService.getStorageTypes()

const Redirect = () => {
  const { user } = useAuth()
  // TODO: remove this after the A/B test is concluded AG-436
  const { userMetadata = {} } = useCustomerMetadata("antiaging_experiment")

  const { getSearchString } = useQueryState()
  const {
    subscription,
    billingAddresses: [billingAddress] = [],
    redoInitialQuestionnaire = false,
  } = user || {}

  const {
    consultations: [{ type: consultationType, status: consultationStatus }] = [
      {},
    ],
  } = subscription || {}

  const initialTreatmentPlanRedirectDisabled = storageService.getItem({
    id: storageKeys.DISABLE_INITIAL_TREATMENT_PLAN_REDIRECT,
    storageType: storageTypes.SESSION,
  })

  const {
    isLoading: isCurrentOrderSummaryLoading,
    data: { data: { lineItems: currentSummaryItems = [] } = {} } = {},
  } = useQuery("currentOrderSummary", orderClient.getCurrentSummary, {
    enabled: !!user,
  })

  const { data: healthData, isLoading: isLoadingHealthData } = useQuery(
    "healthRecord",
    userClient.getHealthRecord,
    {
      enabled: !!user && !initialTreatmentPlanRedirectDisabled,
    }
  )

  const isReturningUser =
    !!user && differenceInDays(new Date(), new Date(user.createdAt)) > 0

  useEffect(() => {
    return () => {
      if (user) {
        storageService.removeItem({
          id: storageKeys.DISABLE_INITIAL_TREATMENT_PLAN_REDIRECT,
          storageType: storageTypes.SESSION,
        })
      }
    }
  }, [user])

  // TODO: Remove VWO-related code once it has been implemented in production or when the decision to remove it has been made
  useEffect(() => {
    if (subscription && consultationStatus === "created") {
      storageService.removeItem({
        id: storageKeys.VWO_THEME_VARIANT,
      })
    }
  }, [subscription, consultationStatus])

  if (isCurrentOrderSummaryLoading || isLoadingHealthData) {
    return <Spinner isFullPage />
  }

  if (!user) {
    navigate(Routes.App.Start.Questionnaire.url + getSearchString(), {
      replace: true,
    })
    return null
  }

  if (!subscription) {
    // TODO: remove this after the A/B test is concluded AG-436
    // *this won't trigger a VWO campaign per se
    if (
      userMetadata.antiaging_experiment?.testPresetType &&
      !AB_TESTS_DATA[AB_TESTS.AntiAging]?.deactivated
    ) {
      const {
        testPresetType,
        test: testName,
        testVariant,
        questionnaireId,
      } = userMetadata.antiaging_experiment

      storageService.setItem({
        id: testName,
        data: testVariant,
      })

      storageService.setItem({
        id: `${testName}_running`,
        data: true,
      })

      storageService.setItem({
        id: storageKeys.VWO_TEST_PRESET_TYPE,
        data: testPresetType,
      })

      if (questionnaireId) {
        storageService.setItem({
          id: storageKeys.VWO_INITIAL_QUESTIONNARE_ID,
          data: questionnaireId,
        })
      }
    }

    if (redoInitialQuestionnaire || initialConsultationService.isEmpty()) {
      navigate(Routes.App.Start.Questionnaire.url, { replace: true })
      return null
    }

    if (isReturningUser) {
      navigate(Routes.App.Start.WelcomeBack.url, { replace: true })

      return null
    }

    if (healthData && !initialTreatmentPlanRedirectDisabled) {
      navigate(`${Routes.App.Start.InitialTreatmentPlan.url}`, {
        replace: true,
      })
      return null
    }

    if (!currentSummaryItems?.length) {
      navigate(Routes.App.Start.ChoosePlan.url, { replace: true })
      return null
    }

    if (!billingAddress || user.isDummy) {
      navigate(Routes.App.Start.Shipping.url, { replace: true })
      return null
    }

    navigate(Routes.App.Start.Payment.url, { replace: true })
    return null
  }

  if (
    consultationType === "initial" &&
    consultationStatus === "waitingfileupload"
  ) {
    if (storageService.getItem({ id: storageKeys.BVDD_PRAXIS })) {
      navigate(Routes.App.Start.Documents.url, { replace: true })
    } else {
      navigate(Routes.App.Start.Photos.url, { replace: true })
    }
    return null
  }

  navigate(Routes.App.url, { replace: true })
  return null
}

const RouteGuard = (props) => (
  <GlobalRouteGuard fallback={Redirect} {...props} />
)

const Start = () => {
  const location = useLocation()

  const { user } = useAuth()

  const antiAgingExperiment = vwoService.useVWOExperimentStatus(
    AB_TESTS.AntiAging
  )

  const {
    userType,
    redoInitialQuestionnaire,
    subscription,
    billingAddresses: [billingAddress] = [],
  } = user || {}

  const {
    consultations: [{ type: consultationType, status: consultationStatus }] = [
      {},
    ],
  } = subscription || {}

  const {
    isLoading: isCurrentOrderSummaryLoading,
    data: { data: { lineItems: currentSummaryItems = [] } = {} } = {},
  } = useQuery("currentOrderSummary", orderClient.getCurrentSummary, {
    enabled: !!user,
  })

  const { isLoading: isInitialConsultationLoading } = useQuery(
    "initialConsultation",
    consultationClient.getInitialConsultation,
    {
      enabled: !!user && initialConsultationService.isEmpty(),
      onSuccess: initialConsultationService.save,
    }
  )

  const canAccessQuestionnaire = useCallback(
    () =>
      !user ||
      user?.redoInitialQuestionnaire ||
      initialConsultationService.isEmpty(),
    [user]
  )

  const canAccessCreateAccount = useCallback(
    ({ token }) => token && !user,
    [user]
  )

  const canAccessInitialTreatmentPlan = useCallback(() => {
    return user && !subscription
  }, [user, subscription])

  const canAccessChoosePlan = useCallback(
    () => user && !subscription && !user.redoInitialQuestionnaire,
    [subscription, user]
  )

  const canAccessShipping = useCallback(
    () =>
      user &&
      !subscription &&
      currentSummaryItems.length > 0 &&
      !user.redoInitialQuestionnaire,
    [subscription, user, currentSummaryItems]
  )

  const canAccessPayment = useCallback(
    async () =>
      !subscription &&
      billingAddress &&
      currentSummaryItems.length > 0 &&
      !user.redoInitialQuestionnaire &&
      !user.isDummy,
    [billingAddress, currentSummaryItems, subscription, user]
  )

  const canAccessPhotos = useCallback(
    () =>
      consultationType === "initial" &&
      consultationStatus === "waitingfileupload",
    [consultationStatus, consultationType]
  )

  const canAccessDocuments = useCallback(
    () =>
      consultationType === "initial" &&
      consultationStatus === "waitingfileupload" &&
      storageService.getItem({ id: storageKeys.BVDD_PRAXIS }),
    [consultationType, consultationStatus]
  )

  // TODO @fox: implement differently
  const canAccessTofuWelcome = useCallback(
    () =>
      userType === "tofu" &&
      !redoInitialQuestionnaire &&
      initialConsultationService.isEmpty(),
    [userType, redoInitialQuestionnaire]
  )

  const canAccessWelcomeBack = useCallback(() => {
    const isReturningUser =
      !!user && differenceInDays(new Date(), new Date(user.createdAt)) > 0

    return isReturningUser && !subscription
  }, [user, subscription])

  const stepConditions = useMemo(
    () => [
      () => !!user,
      () => currentSummaryItems.length > 0,
      () => !!billingAddress,
    ],
    [user, currentSummaryItems, billingAddress]
  )

  if (isCurrentOrderSummaryLoading || isInitialConsultationLoading) {
    return <Spinner isFullPage />
  }

  return (
    <Layout enableConfirmation>
      {(location.pathname === Routes.App.Start.url ||
        location.pathname === Routes.App.Start.Questionnaire.url) && (
        <Seo
          title=""
          meta={[{ key: "robots", name: "robots", content: "noindex" }]}
        />
      )}

      <Container>
        <ABTestContainer
          testName={AB_TESTS.ProgressBarEnabled}
          variants={[[1, ProgressBar]]}
          fallbackComponent={null}
          stepConditions={stepConditions}
        />
        <Router>
          {!["main", "master"].includes(envDetails.gitBranch) && (
            <EnvDetails path={"/env"} />
          )}

          <RouteGuard
            path={Routes.App.Start.Questionnaire.path}
            condition={canAccessQuestionnaire}
            component={Questionnaire}
          />
          <RouteGuard
            path={`${Routes.App.Start.CreateAccount.path}/:token`}
            condition={canAccessCreateAccount}
            component={CreateAccount}
          />

          <RouteGuard
            path={Routes.App.Start.InitialTreatmentPlan.path}
            condition={canAccessInitialTreatmentPlan}
            component={TreatmentPlanNavigation}
          />

          {/* TODO: clean up after A/B test is concluded AG-436 */}
          {antiAgingExperiment.isRunning ? (
            <RouteGuard
              path={Routes.App.Start.ChoosePlan.path}
              condition={canAccessChoosePlan}
              component={AbTestAntiAgingChoosePlanContainer}
            />
          ) : (
            //  TODO @julianna: clean up after A/B test is concluded AG-455
            <RouteGuard
              path={Routes.App.Start.ChoosePlan.path}
              condition={canAccessChoosePlan}
              component={AbTestChoosePlanStorylineImprovementContainer}
            />
          )}

          <RouteGuard
            path={Routes.App.Start.Shipping.path}
            condition={canAccessShipping}
            component={Shipping}
          />

          {/* TODO: clean up after A/B test is concluded AG-436 */}
          <RouteGuard
            path={Routes.App.Start.Payment.path}
            condition={canAccessPayment}
            component={AbTestAntiAgingPaymentContainer}
          />

          <RouteGuard
            path={Routes.App.Start.Documents.path}
            condition={canAccessDocuments}
            component={DocumentsUpload}
          />
          <RouteGuard
            path={Routes.App.Start.Photos.path}
            condition={canAccessPhotos}
            component={Photos}
          />

          <RouteGuard
            path={Routes.App.Start.WelcomeBack.path}
            condition={canAccessWelcomeBack}
            component={WelcomeBack}
          />

          {/* TODO @fox: make sure to move/remove this route from here*/}
          <RouteGuard
            path={Routes.App.Start.TofuWelcome.path}
            condition={canAccessTofuWelcome}
            component={AppSelectionScreen}
          />

          <Redirect default />
        </Router>
      </Container>
    </Layout>
  )
}

// TODO: clean up after A/B test is concluded AG-436
const AbTestAntiAgingChoosePlanContainer = memo(() => (
  <ABTestContainer
    testName={AB_TESTS.AntiAging}
    variants={[
      [1, AntiAgingChoosePlan],
      [2, AntiAgingChoosePlan],
    ]}
    fallbackComponent={ChoosePlan} // TODO: change to ChoosePlan when the experiment is concluded
  />
))

// TODO: clean up after A/B test is concluded AG-455
const AbTestChoosePlanStorylineImprovementContainer = memo(() => (
  <ABTestContainer
    testName={AB_TESTS.PlanSelectionStorylineImprovement}
    variants={[
      [1, PlanSelectionStorylineImprovementV1],
      [2, PlanSelectionStorylineImprovementV2],
      [3, PlanSelectionStorylineImprovementV3],
    ]}
    fallbackComponent={ChoosePlan}
  />
))

// TODO: clean up after A/B test is concluded AG-436
const AbTestAntiAgingPaymentContainer = memo(() => (
  <ABTestContainer
    testName={AB_TESTS.AntiAging}
    variants={[
      [1, AntiAgingPayment],
      [2, AntiAgingPayment],
    ]}
    fallbackComponent={Payment} // TODO: change to ChoosePlan when the experiment is concluded
  />
))

AbTestAntiAgingChoosePlanContainer.displayName =
  "AbTestAntiAgingChoosePlanContainer"
AbTestChoosePlanStorylineImprovementContainer.displayName =
  "AbTestChoosePlanStorylineImprovementContainer"
AbTestAntiAgingPaymentContainer.displayName = "AbTestAntiAgingPaymentContainer"

const EnvDetails = () => {
  return <pre>{JSON.stringify(envDetails)}</pre>
}

export default Start
